import React, { useState, useEffect, useRef } from 'react';
import { Header } from '../components/layout/Header';
import { Button } from '../components/shared/Button';
import { TimerCard } from '../components/timer/TimerCard';
import { CreateTimerModal } from '../components/timer/CreateTimerModal';
import { SessionHistoryPanel } from '../components/session/SessionHistoryPanel';
import { useTimerStore } from '../stores/timerStore';
import { useFirebaseSync } from '../hooks/useFirebaseSync';
import { getServerTime } from '../utils/serverTime';
import {
  isAudioMuted,
  setAudioMuted,
  playCountdownFinishedSound,
  playUntrackedWarningSound,
} from '../utils/audioAlert';

import type { Direction } from '../hooks/useKonamiCode';

interface RoomPageProps {
  roomId: string;
  onLeave: () => void;
  onDirectionClick?: (dir: Direction) => void;
  progressLength?: number;
}

export function RoomPage({ roomId, onLeave, onDirectionClick, progressLength }: RoomPageProps) {
  const {
    timers,
    history,
    activityLog,
    currentRoom,
    isCreateModalOpen,
    setCreateModalOpen,
    participants,
  } = useTimerStore();

  const {
    createTimer,
    startTimer,
    pauseTimer,
    stopTimer,
    resetTimer,
    renameTimer,
    deleteTimer,
    toggleFreePlay,
  } = useFirebaseSync(roomId);

  // Periodic tick for real-time countdown expiration detection
  const [now, setNow] = useState(() => getServerTime());
  useEffect(() => {
    const interval = setInterval(() => setNow(getServerTime()), 1000);
    return () => clearInterval(interval);
  }, []);

  const timerEntries = Object.entries(timers).sort(
    ([, a], [, b]) => a.order - b.order
  );

  const onlineParticipants = Object.values(participants).filter((p) => p.online);

  // Expired countdowns for staff alert banner
  const expiredCountdowns = timerEntries.filter(([, t]) => {
    if (!t || t.type !== 'countdown' || !t.countdownDurationMs) return false;
    if (t.state === 'running' && t.startedAt !== null) {
      const elapsed = (t.accumulatedMs || 0) + (now - t.startedAt);
      return elapsed >= t.countdownDurationMs;
    }
    return false;
  });

  // Untracked play alerts (game running without timer >= 2 minutes)
  const UNTRACKED_PLAY_THRESHOLD_MS = 2 * 60 * 1000;
  const untrackedPlayTimers = timerEntries.filter(([, t]) => {
    if (!t) return false;
    if (t.freePlayMode) return false;
    if (t.state === 'running') return false;
    if (!t.activeGame || !t.activeGame.detectedAt) return false;
    return (now - t.activeGame.detectedAt) >= UNTRACKED_PLAY_THRESHOLD_MS;
  });

  // Gestion du statut audio & avertisseurs sonores (Web Audio API)
  const [isMuted, setIsMuted] = useState(() => isAudioMuted());
  const alertedExpiredIds = useRef<Set<string>>(new Set());
  const alertedUntrackedIds = useRef<Set<string>>(new Set());

  // Déclenchement carillon sonore : compte à rebours terminé (00:00)
  useEffect(() => {
    const currentExpiredIds = new Set(expiredCountdowns.map(([id]) => id));

    for (const [id] of expiredCountdowns) {
      if (!alertedExpiredIds.current.has(id)) {
        playCountdownFinishedSound();
        alertedExpiredIds.current.add(id);
      }
    }

    // Réinitialisation dès qu'un timer n'est plus expiré (stoppé, reset)
    for (const id of Array.from(alertedExpiredIds.current)) {
      if (!currentExpiredIds.has(id)) {
        alertedExpiredIds.current.delete(id);
      }
    }
  }, [expiredCountdowns]);

  // Déclenchement alerte sonore : jeu détecté hors chrono (> 2 min)
  useEffect(() => {
    const currentUntrackedIds = new Set(untrackedPlayTimers.map(([id]) => id));

    for (const [id] of untrackedPlayTimers) {
      if (!alertedUntrackedIds.current.has(id)) {
        playUntrackedWarningSound();
        alertedUntrackedIds.current.add(id);
      }
    }

    for (const id of Array.from(alertedUntrackedIds.current)) {
      if (!currentUntrackedIds.has(id)) {
        alertedUntrackedIds.current.delete(id);
      }
    }
  }, [untrackedPlayTimers]);

  const handleToggleMute = () => {
    const nextState = !isMuted;
    setIsMuted(nextState);
    setAudioMuted(nextState);
  };

  const handleCopyCode = () => {
    if (currentRoom?.code) {
      navigator.clipboard.writeText(currentRoom.code);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
  };

  if (!currentRoom) {
    return (
      <div className="min-h-screen bg-namm-bg flex items-center justify-center">
        <div className="text-center">
          <div className="text-namm-muted font-mono text-sm animate-pulse">
            Connexion à la room...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-namm-bg flex flex-col">
      <Header
        showRoomInfo
        onDirectionClick={onDirectionClick}
        progressLength={progressLength}
      />

      <main className="flex-1 max-w-7xl mx-auto w-full px-5 py-8">
        {/* Room toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8 pb-4 border-b border-namm-line/25">
          <div className="flex items-center gap-4">
            <Button variant="primary" onClick={() => setCreateModalOpen(true)}>
              + Nouveau Timer
            </Button>
            <Button variant="ghost" size="sm" onClick={handleCopyCode} title="Copier le code">
              📋 {currentRoom.code}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleCopyLink}>
              🔗 Copier le lien
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggleMute}
              title={isMuted ? 'Activer le son des alertes' : 'Couper le son des alertes'}
              className={isMuted ? 'text-namm-muted' : 'text-namm-cyan border-namm-cyan/40 bg-namm-cyan/5'}
            >
              {isMuted ? '🔇 Muet' : '🔊 Son actif'}
            </Button>
          </div>

          <div className="flex items-center gap-4">
            {/* Online participants */}
            <div className="flex items-center -space-x-2">
              {onlineParticipants.slice(0, 5).map((p) => (
                <div
                  key={p.pseudo}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-bold text-namm-bg border-2 border-namm-bg"
                  style={{ backgroundColor: p.color }}
                  title={p.pseudo}
                >
                  {p.pseudo.charAt(0).toUpperCase()}
                </div>
              ))}
              {onlineParticipants.length > 5 && (
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-mono text-namm-muted bg-namm-panel-soft border-2 border-namm-bg">
                  +{onlineParticipants.length - 5}
                </div>
              )}
            </div>

            <Button variant="ghost" size="sm" onClick={onLeave}>
              ← Quitter
            </Button>
          </div>
        </div>

        {/* Visual Staff Alert Banner - Expired Countdowns */}
        {expiredCountdowns.length > 0 && (
          <div className="mb-4 p-4 border border-namm-danger/60 bg-gradient-to-r from-namm-danger/20 via-namm-panel/95 to-namm-danger/10 flex flex-wrap items-center justify-between gap-4 shadow-[0_0_24px_rgba(255,59,59,0.2)] animate-pulse">
            <div className="flex items-center gap-3">
              <span className="text-xl">🚨</span>
              <div className="font-mono text-xs">
                <span className="font-bold text-namm-danger uppercase tracking-wider">
                  ALERTE TEMPS ÉCOULÉ ({expiredCountdowns.length} poste{expiredCountdowns.length > 1 ? 's' : ''}) :
                </span>{' '}
                <span className="text-namm-text font-semibold">
                  Session terminée sur {expiredCountdowns.map(([, t]) => `"${t.name}"`).join(', ')} !
                </span>
              </div>
            </div>
            <span className="text-[10px] font-mono uppercase tracking-widest text-namm-danger border border-namm-danger/50 px-2.5 py-1 bg-namm-danger/10 shrink-0">
              Intervention Requise
            </span>
          </div>
        )}

        {/* Visual Staff Alert Banner - Untracked Play (Game running without active timer) */}
        {untrackedPlayTimers.length > 0 && (
          <div className="mb-6 p-4 border border-amber-500/70 bg-gradient-to-r from-amber-500/20 via-namm-panel/95 to-amber-500/10 flex flex-wrap items-center justify-between gap-4 shadow-[0_0_24px_rgba(245,158,11,0.25)] animate-pulse">
            <div className="flex items-center gap-3">
              <span className="text-xl">⚠️</span>
              <div className="font-mono text-xs">
                <span className="font-bold text-amber-400 uppercase tracking-wider">
                  ALERTE JEU HORS CHRONO ({untrackedPlayTimers.length} poste{untrackedPlayTimers.length > 1 ? 's' : ''}) :
                </span>{' '}
                <span className="text-namm-text font-semibold">
                  {untrackedPlayTimers.map(([, t]) => {
                    const mins = Math.floor((now - (t.activeGame?.detectedAt || now)) / 60000);
                    return `"${t.name}" (${t.activeGame?.friendlyName} depuis ${mins} min)`;
                  }).join(', ')}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {untrackedPlayTimers.map(([id, t]) => (
                <Button
                  key={id}
                  variant="primary"
                  size="sm"
                  onClick={() => startTimer(id)}
                  title={`Démarrer le chrono pour ${t.name}`}
                >
                  ▶ Démarrer {t.name}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Timer grid */}
        {timerEntries.length === 0 ? (
          <div className="text-center py-24">
            <div className="text-namm-muted font-mono text-sm mb-4">
              Aucun chronomètre dans cette room
            </div>
            <Button variant="secondary" onClick={() => setCreateModalOpen(true)}>
              Créer le premier timer
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {timerEntries.map(([id, timer]) => (
              <TimerCard
                key={id}
                timerId={id}
                timer={timer}
                onStart={startTimer}
                onPause={pauseTimer}
                onStop={stopTimer}
                onReset={resetTimer}
                onRename={renameTimer}
                onDelete={deleteTimer}
                onToggleFreePlay={toggleFreePlay}
              />
            ))}
          </div>
        )}
      </main>

      {/* History and Activity panel (Read-only audit for room users) */}
      <SessionHistoryPanel
        history={history}
        activityLog={activityLog}
      />

      {/* Create modal */}
      <CreateTimerModal
        isOpen={isCreateModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreate={createTimer}
      />
    </div>
  );
}
